'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import { styled } from '@effektify/theme';
import MuiDialog, { DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import MuiDialogActions from '@mui/material/DialogActions';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogContentText from '@mui/material/DialogContentText';
import MuiDialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { Theme } from 'src/Theme';
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox';
import useTranslations from 'src/hooks/useTranslations';
import { useLayoutEffect, useRef } from 'react';
interface DialogActions {
  ok: string;
  cancel: string;
}
interface DialogProps {
  open?: boolean;
  title: string;
  description?: string;
  actions?: DialogActions;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  customFooter?: React.ReactNode;
  handleClose: (result: boolean | undefined) => void;
  slots?: MuiDialogProps['slots'];
}
const StyledDialog = styled(MuiDialog)<MuiDialogProps>(() => ({}));
const StyledDialogContent = styled(MuiDialogContent)(() => ({
  padding: 0,
  whiteSpace: 'pre-wrap'
}));
const StyledDescription = styled(MuiDialogContentText)(() => ({}));
const StyledDialogTitle = styled(MuiDialogTitle)(() => ({
  fontSize: '1.25rem',
  fontWeight: 500,
  padding: 0
}));
const StyledDialogActions = styled(MuiDialogActions)(({
  theme
}) => ({
  padding: theme.sizes.spacing['07'],
  backgroundColor: theme.colors.surface.secondary,
  borderTop: `1px solid ${theme.colors.border.primary}`,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.sizes.spacing['04']
}));
const Dialog: React.FC<DialogProps> = t0 => {
  const $ = _c(40);
  let children;
  let customFooter;
  let handleClose;
  let icon;
  let props;
  let t1;
  let t2;
  let t3;
  let title;
  if ($[0] !== t0) {
    ({
      open: t1,
      title,
      description: t2,
      actions: t3,
      children,
      icon,
      customFooter,
      handleClose,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = customFooter;
    $[3] = handleClose;
    $[4] = icon;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = title;
  } else {
    children = $[1];
    customFooter = $[2];
    handleClose = $[3];
    icon = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    title = $[9];
  }
  const open = t1 === undefined ? false : t1;
  const description = t2 === undefined ? "" : t2;
  const actions = t3 === undefined ? null : t3;
  const {
    t
  } = useTranslations();
  const restoreFocusRef = useRef(null);
  let t4;
  let t5;
  if ($[10] !== open) {
    t4 = () => {
      if (open && document.activeElement instanceof HTMLElement) {
        restoreFocusRef.current = document.activeElement;
      }
    };
    t5 = [open];
    $[10] = open;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useLayoutEffect(t4, t5);
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      onExit: _temp,
      onExited: () => {
        const target = restoreFocusRef.current;
        restoreFocusRef.current = null;
        if (target?.isConnected && (!document.activeElement || document.activeElement === document.body)) {
          target.focus();
        }
      }
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const transitionFocusHandlers = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      transition: transitionFocusHandlers
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== title) {
    t8 = <StyledDialogTitle id="dialog-title">{title}</StyledDialogTitle>;
    $[15] = title;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== description) {
    t9 = <StyledDescription id="dialog-description">{description}</StyledDescription>;
    $[17] = description;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== children || $[20] !== t9) {
    t10 = <StyledDialogContent>{t9}{children}</StyledDialogContent>;
    $[19] = children;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8) {
    t11 = <FlexColumn sx={_temp3}>{t8}{t10}</FlexColumn>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== icon || $[26] !== t11) {
    t12 = <FlexRow sx={_temp2}>{icon}{t11}</FlexRow>;
    $[25] = icon;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== actions || $[29] !== customFooter || $[30] !== handleClose || $[31] !== t) {
    t13 = customFooter ?? (actions && actions !== null ? (() => Object.entries(actions).map(t14 => {
      const [type, label] = t14;
      return <Button variant={type === "cancel" ? "text" : "contained"} key={`button-${type}`} onClick={() => handleClose(type === "ok")}>{label}</Button>;
    }))() : <Button onClick={() => handleClose(false)}>{t("common.close")}</Button>);
    $[28] = actions;
    $[29] = customFooter;
    $[30] = handleClose;
    $[31] = t;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t13) {
    t14 = <StyledDialogActions>{t13}</StyledDialogActions>;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== open || $[36] !== props || $[37] !== t12 || $[38] !== t14) {
    t15 = <StyledDialog keepMounted={true} disableRestoreFocus={true} slotProps={t7} open={open} aria-labelledby="dialog-title" aria-describedby="dialog-description" {...props}>{t12}{t14}</StyledDialog>;
    $[35] = open;
    $[36] = props;
    $[37] = t12;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
};
export default Dialog;
export type { DialogProps };
function _temp(node) {
  if (document.activeElement instanceof HTMLElement && node.contains(document.activeElement)) {
    document.activeElement.blur();
  }
}
function _temp2(theme) {
  return {
    padding: theme.sizes.spacing["09"],
    gap: theme.sizes.spacing["07"]
  };
}
function _temp3(theme_0) {
  return {
    gap: theme_0.sizes.spacing["07"]
  };
}